import { useEffect, useMemo, useState } from 'react';
import {
  acknowledgeAllErrorAlerts,
  acknowledgeErrorAlert,
  fetchErrorAlerts,
  saveErrorAlertConfig,
  type ErrorAlertConfig,
  type PlatformErrorAlert,
} from '@/lib/platformLogs';
import { openErrorPage, prettyPage } from './logUtils';

interface ErrorAlertsPanelProps {
  alerts: PlatformErrorAlert[];
  config: ErrorAlertConfig;
  onConfigSaved: (config: ErrorAlertConfig) => void;
  onRefresh: () => void;
  onFocusPage: (page: string) => void;
}

type AlertPeriod = '24h' | '7d' | '30d' | 'all';

const PERIODS: { key: AlertPeriod; label: string }[] = [
  { key: '24h', label: '24 h' },
  { key: '7d', label: '7 j' },
  { key: '30d', label: '30 j' },
  { key: 'all', label: 'Tout' },
];

function sinceMs(period: AlertPeriod): number {
  if (period === 'all') return 0;
  const hours = period === '24h' ? 24 : period === '7d' ? 24 * 7 : 24 * 30;
  return Date.now() - hours * 3600 * 1000;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `il y a ${hours} h`;
  return `il y a ${Math.round(hours / 24)} j`;
}

function fullDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function ErrorAlertsPanel({ alerts, config, onConfigSaved, onRefresh, onFocusPage }: ErrorAlertsPanelProps) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draft, setDraft] = useState<ErrorAlertConfig>(config);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [working, setWorking] = useState<number | null>(null);

  const [period, setPeriod] = useState<AlertPeriod>('24h');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<PlatformErrorAlert[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const openSettings = () => {
    setDraft(config);
    setMessage(null);
    setSettingsOpen(true);
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const rows = await fetchErrorAlerts(false, 100);
      setHistory(rows.filter((a) => a.acknowledged));
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (historyOpen && history.length === 0) void loadHistory();
  }, [historyOpen, history.length]);

  const visibleAlerts = useMemo(() => {
    const from = sinceMs(period);
    return alerts.filter((a) => new Date(a.created_at).getTime() >= from);
  }, [alerts, period]);

  const visibleHistory = useMemo(() => {
    const from = sinceMs(period);
    return history.filter((a) => new Date(a.acknowledged_at || a.created_at).getTime() >= from);
  }, [history, period]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await saveErrorAlertConfig(draft);
      onConfigSaved(draft);
      setMessage('Seuil enregistré');
      setTimeout(() => setSettingsOpen(false), 700);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Impossible d'enregistrer le seuil");
    } finally {
      setSaving(false);
    }
  };

  const handleAck = async (id: number) => {
    setWorking(id);
    try {
      await acknowledgeErrorAlert(id);
      onRefresh();
      void loadHistory();
    } catch {
      /* best-effort */
    } finally {
      setWorking(null);
    }
  };

  const handleAckAll = async () => {
    try {
      await acknowledgeAllErrorAlerts();
      onRefresh();
      void loadHistory();
    } catch {
      /* best-effort */
    }
  };

  return (
    <div className="mb-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h3 className="text-xs font-semibold text-foreground-700 flex items-center gap-2">
          <i className={`ri-alarm-warning-line ${visibleAlerts.length > 0 ? 'text-red-500' : 'text-accent-500'}`}></i>
          Alertes automatiques
          <span className="text-[10px] font-normal text-foreground-400">
            seuil : {config.threshold} erreur(s) / {config.windowMinutes} min sur une même page
          </span>
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`px-2.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  period === p.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {visibleAlerts.length > 0 && (
            <button
              onClick={handleAckAll}
              className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-check-double-line"></i>Tout acquitter
            </button>
          )}
          <button
            onClick={openSettings}
            className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium bg-secondary-100 text-secondary-900 hover:bg-secondary-200/70 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-equalizer-line"></i>Régler le seuil
          </button>
        </div>
      </div>

      {visibleAlerts.length === 0 ? (
        <div className="rounded-lg border border-background-200/70 bg-background-50 px-4 py-3 flex items-center gap-2">
          <i className="ri-shield-check-line text-accent-500"></i>
          <p className="text-[11px] text-foreground-500">
            Aucune page ne dépasse le seuil d’erreurs sur cette période. Une alerte apparaîtra ici dès qu’une page dépassera
            {' '}
            {config.threshold} erreur(s) en {config.windowMinutes} minutes.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {visibleAlerts.map((alert) => (
            <div key={alert.id} className="rounded-lg border border-red-200/70 bg-red-50/60 p-3 md:p-4">
              <div className="flex flex-col md:flex-row md:items-start gap-3">
                <span className="w-8 h-8 rounded-lg bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-sm"></i>
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-foreground-950">{prettyPage(alert.page)}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold whitespace-nowrap">
                      {alert.occurrences} erreurs / {alert.window_minutes} min
                    </span>
                    <span className="text-[10px] text-foreground-500 whitespace-nowrap">seuil {alert.threshold}</span>
                    <span className="text-[10px] text-foreground-400 whitespace-nowrap">{relativeTime(alert.last_seen_at)}</span>
                  </div>
                  <p className="text-[11px] text-foreground-700 break-words line-clamp-2">{alert.sample_message || 'Erreur sans message'}</p>
                  <p className="text-[10px] text-foreground-400 font-mono mt-0.5 break-all">{alert.page}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => openErrorPage({ page: alert.page })}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-external-link-line"></i>Ouvrir la page
                  </button>
                  <button
                    onClick={() => onFocusPage(alert.page)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium bg-background-50 border border-background-200/70 text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-list-check-2"></i>Voir les incidents
                  </button>
                  <button
                    onClick={() => handleAck(alert.id)}
                    disabled={working === alert.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    <i className="ri-check-line"></i>Acquitter
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Historique des alertes acquittées */}
      <div className="mt-2">
        <button
          onClick={() => setHistoryOpen((v) => !v)}
          className="flex items-center gap-2 text-[11px] font-medium text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer"
        >
          <i className={historyOpen ? 'ri-eye-off-line' : 'ri-history-line'}></i>
          Historique des alertes acquittées
          {history.length > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-600 text-[10px] font-semibold">
              {visibleHistory.length}
            </span>
          )}
          <i className={historyOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
        </button>

        {historyOpen && (
          <div className="mt-2 rounded-lg border border-background-200/70 bg-background-50 overflow-hidden">
            {historyLoading ? (
              <div className="flex items-center justify-center py-6">
                <i className="ri-loader-4-line animate-spin text-lg text-primary-500"></i>
              </div>
            ) : visibleHistory.length === 0 ? (
              <p className="px-4 py-3 text-[11px] text-foreground-500">
                Aucune alerte acquittée sur cette période.
              </p>
            ) : (
              <div className="divide-y divide-background-200/60 max-h-72 overflow-auto">
                {visibleHistory.map((alert) => (
                  <div key={alert.id} className="flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-2">
                    <span className="w-6 h-6 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-[11px]"></i>
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium text-foreground-800 truncate">{prettyPage(alert.page)}</p>
                      <p className="text-[10px] text-foreground-500 truncate">{alert.sample_message || 'Erreur sans message'}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                      <span className="text-[10px] text-foreground-500 whitespace-nowrap">
                        {alert.occurrences} erreurs · seuil {alert.threshold}
                      </span>
                      <span className="text-[10px] text-foreground-400 whitespace-nowrap">
                        acquittée {fullDate(alert.acknowledged_at || alert.created_at)}
                      </span>
                      <button
                        onClick={() => openErrorPage({ page: alert.page })}
                        className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:bg-background-100 hover:text-primary-700 transition-colors cursor-pointer"
                        title="Ouvrir la page"
                      >
                        <i className="ri-external-link-line text-[11px]"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between px-4 py-2 border-t border-background-200/70 bg-background-100/40">
              <span className="text-[10px] text-foreground-400">Les 100 dernières alertes acquittées</span>
              <button
                onClick={() => void loadHistory()}
                className="text-[10px] text-foreground-600 hover:text-foreground-900 flex items-center gap-1 cursor-pointer"
              >
                <i className={`ri-refresh-line ${historyLoading ? 'animate-spin' : ''}`}></i>Rafraîchir
              </button>
            </div>
          </div>
        )}
      </div>

      {settingsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="w-full max-w-md rounded-lg bg-background-50 border border-background-200/70 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-background-200/70">
              <h4 className="text-sm font-semibold text-foreground-950 flex items-center gap-2">
                <i className="ri-equalizer-line text-primary-500"></i>
                Seuil d’alerte des erreurs
              </h4>
              <button
                onClick={() => setSettingsOpen(false)}
                className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="p-4 space-y-3">
              <label className="block">
                <span className="text-[11px] font-medium text-foreground-600">Nombre d’erreurs déclenchant l’alerte</span>
                <input
                  type="number"
                  min={1}
                  value={draft.threshold}
                  onChange={(e) => setDraft({ ...draft, threshold: Math.max(1, Number(e.target.value) || 1) })}
                  className="mt-1 w-full h-9 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                />
              </label>
              <label className="block">
                <span className="text-[11px] font-medium text-foreground-600">Sur une fenêtre de (minutes)</span>
                <input
                  type="number"
                  min={1}
                  value={draft.windowMinutes}
                  onChange={(e) => setDraft({ ...draft, windowMinutes: Math.max(1, Number(e.target.value) || 1) })}
                  className="mt-1 w-full h-9 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                />
              </label>
              <label className="block">
                <span className="text-[11px] font-medium text-foreground-600">Ne pas répéter l’alerte pendant (minutes)</span>
                <input
                  type="number"
                  min={0}
                  value={draft.cooldownMinutes}
                  onChange={(e) => setDraft({ ...draft, cooldownMinutes: Math.max(0, Number(e.target.value) || 0) })}
                  className="mt-1 w-full h-9 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                />
              </label>
              <p className="text-[10px] text-foreground-400 leading-relaxed">
                Exemple : 5 erreurs en 10 minutes sur la même page → une alerte apparaît ici et sur « Logs Système » dans le menu.
              </p>
              {message && (
                <p className={`text-[11px] ${message === 'Seuil enregistré' ? 'text-accent-700' : 'text-red-600'}`}>{message}</p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-background-200/70">
              <button
                onClick={() => setSettingsOpen(false)}
                className="px-3 py-1.5 rounded-full text-[11px] font-medium bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-1.5 rounded-full text-[11px] font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}